import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@jambushrushti/integrations/supabase/client";
import { AdminLayout } from "@jambushrushti/components/admin/AdminLayout";
import {
  AdminViewToggle,
  useAdminViewMode,
} from "@jambushrushti/components/admin/AdminViewToggle";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Input } from "@jambushrushti/components/ui/input";
import { Textarea } from "@jambushrushti/components/ui/textarea";
import { Label } from "@jambushrushti/components/ui/label";
import { Switch } from "@jambushrushti/components/ui/switch";
import { Badge } from "@jambushrushti/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@jambushrushti/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@jambushrushti/components/ui/tabs";
import { useToast } from "@jambushrushti/hooks/use-toast";
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Search,
  Eye,
  EyeOff,
  Upload,
  FileText,
  Volume2,
  Music,
} from "lucide-react";
import { format } from "date-fns";
import { cmsFetch } from "@jambushrushti/services/adminAuth";
import {
  IMAGE_ACCEPT,
  toMultipart,
  validateImageFile,
} from "@jambushrushti/lib/multipart";

interface Book {
  id: string;
  title: string;
  author: string | null;
  description: string | null;
  cover_image: string | null;
  category: string | null;
  language: string | null;
  year: number | null;
  is_published: boolean | null;
  pdf_url: string | null;
  audio_hindi: string | null;
  audio_english: string | null;
  audio_sanskrit: string | null;
  audio_prakrit: string | null;
  audio_gujarati: string | null;
  created_at: string;
}

const AUDIO_LANGUAGES = [
  { key: "audio_hindi", label: "Hindi" },
  { key: "audio_english", label: "English" },
  { key: "audio_sanskrit", label: "Sanskrit" },
  { key: "audio_prakrit", label: "Prakrit" },
  { key: "audio_gujarati", label: "Gujarati" },
] as const;

const BOOKS_API_BASE = import.meta.env.DEV
  ? "/jambushrusti-api"
  : (
      import.meta.env.VITE_API_BASE_URL ||
      "https://api.jambushusti.universal-software.in/api"
    ).replace(/\/$/, "");
const BOOKS_ENDPOINT = `${BOOKS_API_BASE}/jambushrusti/cms/books/`;

// Headers managed by cmsFetch

async function parseBooksResponse(response: Response) {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      payload?.detail ||
      payload?.message ||
      payload?.error?.message ||
      payload?.error;
    throw new Error(
      typeof message === "string" ? message : "Books request failed."
    );
  }
  return payload;
}

function normalizeBook(record: Record<string, any>): Book {
  const audioUrl = record.audio_url || "";
  const audioKey = `audio_${String(record.language || "").toLowerCase()}`;
  return {
    id: String(record.id || record.uuid || ""),
    title: String(record.title || ""),
    author: record.author || null,
    description: record.description || null,
    cover_image: record.cover_image || record.cover_image_url || null,
    category: record.category || null,
    language: record.language || null,
    year: record.year == null ? null : Number(record.year),
    is_published: Boolean(record.is_published),
    pdf_url: record.pdf_url || null,
    audio_hindi:
      audioKey === "audio_hindi" ? audioUrl : record.audio_hindi || null,
    audio_english:
      audioKey === "audio_english" ? audioUrl : record.audio_english || null,
    audio_sanskrit:
      audioKey === "audio_sanskrit"
        ? audioUrl
        : record.audio_sanskrit || audioUrl || null,
    audio_prakrit:
      audioKey === "audio_prakrit" ? audioUrl : record.audio_prakrit || null,
    audio_gujarati:
      audioKey === "audio_gujarati" ? audioUrl : record.audio_gujarati || null,
    created_at: record.created_at || new Date(0).toISOString(),
  };
}

export default function BooksManagement() {
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useAdminViewMode();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBook, setEditingBook] = useState<Book | null>(null);
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: "",
    author: "",
    description: "",
    category: "",
    language: "",
    year: "",
    pdf_url: "",
    audio_hindi: "",
    audio_english: "",
    audio_sanskrit: "",
    audio_prakrit: "",
    audio_gujarati: "",
    is_published: true,
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: books = [], isLoading } = useQuery({
    queryKey: ["admin-books"],
    queryFn: async () => {
      const payload = await parseBooksResponse(
        await cmsFetch(BOOKS_ENDPOINT, { method: "GET" })
      );
      const rows =
        payload?.results || payload?.data?.results || payload?.data || payload;
      return (Array.isArray(rows) ? rows : []).map(normalizeBook);
    },
  });

  const toBookPayload = (data: typeof formData) => {
    const languageAudioKey =
      `audio_${data.language.toLowerCase()}` as keyof typeof data;
    const selectedAudio =
      (typeof data[languageAudioKey] === "string" && data[languageAudioKey]) ||
      data.audio_sanskrit ||
      data.audio_hindi ||
      data.audio_english ||
      data.audio_prakrit ||
      data.audio_gujarati ||
      "";
    return {
      title: data.title,
      author: data.author,
      category: data.category,
      language: data.language,
      year: data.year ? Number(data.year) : null,
      description: data.description,
      is_published: data.is_published,
      pdf_url: data.pdf_url,
      audio_url: selectedAudio,
    };
  };

  const handlePdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    if (file.type !== "application/pdf") {
      toast({ title: "Please select a PDF file", variant: "destructive" });
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      toast({
        title: "File size must be less than 50MB",
        variant: "destructive",
      });
      return;
    }

    setUploadingPdf(true);
    try {
      const fileName = `${Date.now()}-${file.name}`;
      const { data, error } = await supabase.storage
        .from("book-pdfs")
        .upload(fileName, file);

      if (error) {
        throw error;
      }

      const {
        data: { publicUrl },
      } = supabase.storage.from("book-pdfs").getPublicUrl(fileName);

      setFormData({ ...formData, pdf_url: publicUrl });
      toast({ title: "PDF uploaded successfully!" });
    } catch (error: any) {
      toast({
        title: "Error uploading PDF",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleAudioUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    languageKey: string
  ) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    if (!file.type.startsWith("audio/")) {
      toast({ title: "Please select an audio file", variant: "destructive" });
      return;
    }

    if (file.size > 100 * 1024 * 1024) {
      toast({
        title: "File size must be less than 100MB",
        variant: "destructive",
      });
      return;
    }

    setUploadingAudio(languageKey);
    try {
      const fileName = `${Date.now()}-${languageKey}-${file.name}`;
      const { data, error } = await supabase.storage
        .from("book-audio")
        .upload(fileName, file);

      if (error) {
        throw error;
      }

      const {
        data: { publicUrl },
      } = supabase.storage.from("book-audio").getPublicUrl(fileName);

      setFormData({ ...formData, [languageKey]: publicUrl });
      toast({ title: "Audio uploaded successfully!" });
    } catch (error: any) {
      toast({
        title: "Error uploading audio",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUploadingAudio(null);
    }
  };

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return parseBooksResponse(
        await cmsFetch(BOOKS_ENDPOINT, {
          method: "POST",
          body: toMultipart(toBookPayload(data), "cover_image", coverImage),
        })
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-books"] });
      queryClient.invalidateQueries({ queryKey: ["books-public"] });
      toast({ title: "Book added successfully!" });
      resetForm();
      setIsDialogOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error adding book",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof formData }) => {
      return parseBooksResponse(
        await cmsFetch(`${BOOKS_ENDPOINT}${id}/`, {
          method: "PATCH",
          body: toMultipart(toBookPayload(data), "cover_image", coverImage),
        })
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-books"] });
      queryClient.invalidateQueries({ queryKey: ["books-public"] });
      toast({ title: "Book updated successfully!" });
      resetForm();
      setIsDialogOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating book",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return parseBooksResponse(
        await cmsFetch(`${BOOKS_ENDPOINT}${id}/`, {
          method: "DELETE",
        })
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-books"] });
      queryClient.invalidateQueries({ queryKey: ["books-public"] });
      toast({ title: "Book deleted successfully!" });
    },
    onError: (error: Error) => {
      toast({
        title: "Error deleting book",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setFormData({
      title: "",
      author: "",
      description: "",
      category: "",
      language: "",
      year: "",
      pdf_url: "",
      audio_hindi: "",
      audio_english: "",
      audio_sanskrit: "",
      audio_prakrit: "",
      audio_gujarati: "",
      is_published: true,
    });
    setEditingBook(null);
    setCoverImage(null);
  };

  const handleEdit = (book: Book) => {
    setEditingBook(book);
    setFormData({
      title: book.title,
      author: book.author || "",
      description: book.description || "",
      category: book.category || "",
      language: book.language || "",
      year: book.year?.toString() || "",
      pdf_url: book.pdf_url || "",
      audio_hindi: book.audio_hindi || "",
      audio_english: book.audio_english || "",
      audio_sanskrit: book.audio_sanskrit || "",
      audio_prakrit: book.audio_prakrit || "",
      audio_gujarati: book.audio_gujarati || "",
      is_published: book.is_published ?? true,
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast({ title: "Title is required", variant: "destructive" });
      return;
    }
    if (editingBook) {
      updateMutation.mutate({ id: editingBook.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const filteredBooks = books.filter(
    book =>
      book.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (book.author || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const hasAudio = (book: Book) => {
    return (
      book.audio_hindi ||
      book.audio_english ||
      book.audio_sanskrit ||
      book.audio_prakrit ||
      book.audio_gujarati
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="font-display text-xl font-bold text-foreground sm:text-2xl">
              Books Management
            </h1>
            <p className="mt-1 text-muted-foreground">
              Add and manage books with PDF and multilingual audio
            </p>
          </div>
          <Dialog
            open={isDialogOpen}
            onOpenChange={open => {
              setIsDialogOpen(open);
              if (!open) {
                resetForm();
              }
            }}
          >
            <DialogTrigger asChild>
              <Button className="w-fit gap-2 md:w-auto">
                <Plus className="h-4 w-4" />
                Add Book
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingBook ? "Edit Book" : "Add New Book"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <Tabs defaultValue="basic" className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="basic">Basic Info</TabsTrigger>
                    <TabsTrigger value="pdf">PDF</TabsTrigger>
                    <TabsTrigger value="audio">Audio</TabsTrigger>
                  </TabsList>

                  <TabsContent value="basic" className="mt-4 space-y-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="sm:col-span-2">
                        <Label>Title *</Label>
                        <Input
                          value={formData.title}
                          onChange={e =>
                            setFormData({ ...formData, title: e.target.value })
                          }
                          placeholder="Book title"
                        />
                      </div>
                      <div>
                        <Label>Author</Label>
                        <Input
                          value={formData.author}
                          onChange={e =>
                            setFormData({ ...formData, author: e.target.value })
                          }
                          placeholder="Author name"
                        />
                      </div>
                      <div>
                        <Label>Category</Label>
                        <Input
                          value={formData.category}
                          onChange={e =>
                            setFormData({
                              ...formData,
                              category: e.target.value,
                            })
                          }
                          placeholder="e.g., Agama, Sutra, Commentary"
                        />
                      </div>
                      <div>
                        <Label>Language</Label>
                        <Input
                          value={formData.language}
                          onChange={e =>
                            setFormData({
                              ...formData,
                              language: e.target.value,
                            })
                          }
                          placeholder="e.g., Sanskrit, Prakrit, Hindi"
                        />
                      </div>
                      <div>
                        <Label>Year</Label>
                        <Input
                          type="number"
                          value={formData.year}
                          onChange={e =>
                            setFormData({ ...formData, year: e.target.value })
                          }
                          placeholder="Publication year"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <Label>Cover Image</Label>
                        <Input
                          type="file"
                          accept={IMAGE_ACCEPT}
                          onChange={e => {
                            const file = e.target.files?.[0] || null;
                            if (!file) {
                              return setCoverImage(null);
                            }
                            try {
                              validateImageFile(file);
                              setCoverImage(file);
                            } catch (error) {
                              e.target.value = "";
                              toast({
                                title: (error as Error).message,
                                variant: "destructive",
                              });
                            }
                          }}
                        />
                        {editingBook?.cover_image && !coverImage && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            Keep the current image, or choose a replacement.
                          </p>
                        )}
                      </div>
                      <div className="sm:col-span-2">
                        <Label>Description</Label>
                        <Textarea
                          value={formData.description}
                          onChange={e =>
                            setFormData({
                              ...formData,
                              description: e.target.value,
                            })
                          }
                          placeholder="Book description..."
                          rows={3}
                        />
                      </div>
                      <div className="flex items-center gap-2 sm:col-span-2">
                        <Switch
                          checked={formData.is_published}
                          onCheckedChange={checked =>
                            setFormData({ ...formData, is_published: checked })
                          }
                        />
                        <Label>Published (visible on website)</Label>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="pdf" className="mt-4 space-y-4">
                    <div className="space-y-4">
                      <div className="rounded-2xl border-2 border-dashed border-heritage-300 bg-heritage-50/40 p-6 text-center transition-all hover:border-heritage-500">
                        <div className="flex flex-col items-center justify-center space-y-3">
                          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-heritage-100 text-heritage-700">
                            <Upload className="h-6 w-6" />
                          </div>
                          <div>
                            <h4 className="text-base font-bold text-umber-900">
                              Upload Document PDF
                            </h4>
                            <p className="mt-1 text-xs text-umber-500">
                              Drag and drop your PDF file here, or click to
                              browse (max 50MB)
                            </p>
                          </div>

                          <div className="w-full max-w-sm pt-2">
                            <Input
                              type="file"
                              accept=".pdf"
                              onChange={handlePdfUpload}
                              disabled={uploadingPdf}
                              className="hidden"
                              id="pdf-file-upload-input"
                            />
                            <Label
                              htmlFor="pdf-file-upload-input"
                              className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl bg-heritage-600 px-5 py-2.5 text-xs font-semibold text-white shadow-md transition-all hover:bg-heritage-700"
                            >
                              {uploadingPdf ? (
                                <>
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  Uploading PDF...
                                </>
                              ) : (
                                <>
                                  <FileText className="h-4 w-4" />
                                  Select PDF File
                                </>
                              )}
                            </Label>
                          </div>

                          {formData.pdf_url && (
                            <div className="mt-3 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs font-semibold text-emerald-800">
                              <FileText className="h-4 w-4 text-emerald-600" />
                              <span className="max-w-xs truncate">
                                {formData.pdf_url.split("/").pop() ||
                                  "PDF Uploaded"}
                              </span>
                              <Badge className="ml-auto bg-emerald-600 text-[10px] text-white">
                                Ready
                              </Badge>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="audio" className="mt-4 space-y-4">
                    <div className="mb-4 rounded-lg border border-dashed border-border bg-secondary/30 p-4">
                      <div className="mb-2 flex items-center gap-3">
                        <Volume2 className="h-6 w-6 text-primary" />
                        <div>
                          <h4 className="font-medium">Multilingual Audio</h4>
                          <p className="text-sm text-muted-foreground">
                            Upload audio files in different languages (max 100MB
                            each)
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      {AUDIO_LANGUAGES.map(({ key, label }) => (
                        <div
                          key={key}
                          className="space-y-2 rounded-lg border p-3"
                        >
                          <div className="flex items-center justify-between">
                            <Label className="flex items-center gap-2">
                              <Music className="h-4 w-4" />
                              {label} Audio
                            </Label>
                            {formData[key as keyof typeof formData] && (
                              <Badge variant="secondary" className="text-xs">
                                <Volume2 className="mr-1 h-3 w-3" />
                                Ready
                              </Badge>
                            )}
                          </div>

                          <div className="flex gap-2">
                            <Input
                              type="file"
                              accept="audio/*"
                              onChange={e => handleAudioUpload(e, key)}
                              disabled={uploadingAudio === key}
                              className="flex-1"
                            />
                            {uploadingAudio === key && (
                              <Loader2 className="h-5 w-5 animate-spin" />
                            )}
                          </div>

                          <Input
                            value={
                              (formData[
                                key as keyof typeof formData
                              ] as string) || ""
                            }
                            onChange={e =>
                              setFormData({
                                ...formData,
                                [key]: e.target.value,
                              })
                            }
                            placeholder={`Or paste ${label} audio URL`}
                            className="text-sm"
                          />
                        </div>
                      ))}
                    </div>
                  </TabsContent>
                </Tabs>

                <div className="flex justify-end gap-3 border-t pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      createMutation.isPending || updateMutation.isPending
                    }
                  >
                    {(createMutation.isPending || updateMutation.isPending) && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    {editingBook ? "Update Book" : "Add Book"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 gap-y-3">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search books..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <AdminViewToggle value={viewMode} onChange={setViewMode} />
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div
            className={
              viewMode === "cards"
                ? "grid gap-4 md:grid-cols-2 xl:grid-cols-3"
                : "grid gap-4"
            }
          >
            {filteredBooks.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-muted-foreground">
                    No books found. Add your first book!
                  </p>
                </CardContent>
              </Card>
            ) : viewMode === "table" ? (
              <div className="overflow-x-auto rounded-xl border bg-card md:col-span-2 xl:col-span-3">
                <table className="w-full text-left text-sm">
                  <thead className="border-b bg-muted/60 text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3">Book</th>
                      <th className="px-4 py-3">Author</th>
                      <th className="px-4 py-3">Category</th>
                      <th className="px-4 py-3">Language</th>
                      <th className="px-4 py-3">Year</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredBooks.map(book => (
                      <tr key={book.id} className="hover:bg-muted/30">
                        <td className="max-w-xs px-4 py-3 font-medium">
                          <span className="line-clamp-1">{book.title}</span>
                        </td>
                        <td className="px-4 py-3">{book.author || "—"}</td>
                        <td className="px-4 py-3">{book.category || "—"}</td>
                        <td className="px-4 py-3">{book.language || "—"}</td>
                        <td className="px-4 py-3">{book.year || "—"}</td>
                        <td className="px-4 py-3">
                          <Badge
                            variant={
                              book.is_published ? "default" : "secondary"
                            }
                          >
                            {book.is_published ? "Published" : "Draft"}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(book)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-destructive"
                              onClick={() =>
                                confirm(
                                  "Are you sure you want to delete this book?"
                                ) && deleteMutation.mutate(book.id)
                              }
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              filteredBooks.map(book => (
                <Card key={book.id} className="overflow-hidden">
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      {book.cover_image && (
                        <img
                          src={book.cover_image}
                          alt={book.title}
                          className="h-28 w-20 shrink-0 rounded-lg object-cover"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                          <div>
                            <h3 className="line-clamp-1 font-semibold text-foreground">
                              {book.title}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {book.author || "Unknown author"}
                            </p>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                            <Badge
                              variant={
                                book.is_published ? "default" : "secondary"
                              }
                            >
                              {book.is_published ? (
                                <Eye className="mr-1 h-3 w-3" />
                              ) : (
                                <EyeOff className="mr-1 h-3 w-3" />
                              )}
                              {book.is_published ? "Published" : "Draft"}
                            </Badge>
                          </div>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {book.category && (
                            <Badge variant="outline">{book.category}</Badge>
                          )}
                          {book.language && (
                            <Badge variant="outline">{book.language}</Badge>
                          )}
                          {book.year && (
                            <Badge variant="outline">{book.year}</Badge>
                          )}
                          {book.pdf_url && (
                            <Badge
                              variant="secondary"
                              className="bg-umber-100 text-umber-700 dark:bg-umber-900/30 dark:text-umber-400"
                            >
                              <FileText className="mr-1 h-3 w-3" />
                              PDF
                            </Badge>
                          )}
                          {hasAudio(book) && (
                            <Badge
                              variant="secondary"
                              className="bg-heritage-100 text-heritage-700 dark:bg-heritage-900/30 dark:text-heritage-400"
                            >
                              <Volume2 className="mr-1 h-3 w-3" />
                              Audio
                            </Badge>
                          )}
                        </div>
                        <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
                          {book.description}
                        </p>
                        <div className="mt-3 flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(book)}
                          >
                            <Pencil className="mr-1 h-3 w-3" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-destructive/40 text-destructive hover:border-destructive hover:bg-destructive hover:text-white"
                            onClick={() => {
                              if (
                                confirm(
                                  "Are you sure you want to delete this book?"
                                )
                              ) {
                                deleteMutation.mutate(book.id);
                              }
                            }}
                          >
                            <Trash2 className="mr-1 h-3 w-3" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
